import { createContext, type ReactNode, use, useRef } from "react";
import { useStore } from "zustand";

import {
  createIdentityStore,
  type IdentityStore,
  type IdentityStoreApi,
} from "@mountayaapp/identity";

/**
 * IdentityContext holds the render-free identity store instance. The provider
 * lives here (a sanctioned JSX carve-out) while the store itself stays in
 * @mountayaapp/identity. NOTE: this identity store is the i18n `ctx` consumed by
 * useTranslation(ctx, …) — distinct from the tool ctx.
 */
const IdentityContext = createContext<IdentityStoreApi | null>(null);

/**
 * IdentityProviderProps optionally inject a pre-built identity store. The SPA
 * passes the module-scoped store it also threads through the router context, so
 * the beforeLoad guards and the React tree share one instance. When omitted
 * (e.g. the docs app, or a test), the provider falls back to creating its own.
 */
interface IdentityProviderProps {
  children: ReactNode;
  store?: IdentityStoreApi;
}

/**
 * IdentityProvider exposes an identity store via context. It uses the injected
 * store when one is supplied, otherwise creates one per mount. The SPA session
 * bootstrap (ensureSession in the root beforeLoad) hydrates the injected store
 * from Ory before the first render.
 */
export const IdentityProvider = ({ children, store }: IdentityProviderProps) => {
  const storeRef = useRef<IdentityStoreApi | null>(store ?? null);
  if (storeRef.current === null) {
    storeRef.current = createIdentityStore();
  }

  return <IdentityContext value={storeRef.current}>{children}</IdentityContext>;
};

/**
 * useIdentityStore selects from the identity store with the given selector (pass
 * useShallow for multi-field selections). Uses the new use(Context) API (never
 * useContext) per the project convention. Throws when called outside an
 * IdentityProvider.
 */
export const useIdentityStore = <T,>(selector: (state: IdentityStore) => T): T => {
  const store = use(IdentityContext);
  if (!store) {
    throw new Error("useIdentityStore must be used within an IdentityProvider");
  }

  return useStore(store, selector);
};

/**
 * useIdentityStoreApi returns the raw render-free identity store handle from
 * context — the same module-scoped instance the SPA also threads through the
 * router context. It is the seam the account-settings flow needs: after an Ory
 * self-service settings submit succeeds, refreshSession re-hydrates the store and
 * takes the IdentityStoreApi handle directly, which useIdentityStore's
 * selector-only signature cannot supply. Throws when called outside an
 * IdentityProvider. Components must not mutate the store through this handle — the
 * one-mutation-surface invariant still routes display writes through the tool bus;
 * this exists only for the auth carve-out (ensureSession owns the session writes).
 */
export const useIdentityStoreApi = (): IdentityStoreApi => {
  const store = use(IdentityContext);
  if (!store) {
    throw new Error("useIdentityStoreApi must be used within an IdentityProvider");
  }

  return store;
};

/**
 * useIsAuthenticated returns whether a viewer is signed in — the identity store
 * carries a user id. A narrow boolean selector keeps consumers from re-rendering
 * on any other slice change. Surfaces that layer behavior on the presence of a
 * session (the share view's plan gating and its "Open Mountaya" call-to-action)
 * read it through this hook so a single derivation owns the anonymous/authenticated
 * split.
 */
export const useIsAuthenticated = (): boolean => useIdentityStore((state) => state.userId !== null);
